import {
  EnvironmentInjector,
  Injectable,
  computed,
  effect,
  inject,
  runInInjectionContext,
  signal
} from '@angular/core';
import { contentToText } from '@portal/app/shared/components/ai-assistant-panel/helpers/chat-content.helpers';
import {
  buildDisplayBlocks,
  buildMessages,
  findFinalizeTarget,
  liveToolCallsFor
} from '@portal/app/shared/components/ai-assistant-panel/helpers/chat-projection.helpers';
import type {
  ChatBlock,
  ChatMessage,
  MessageMeta,
  StreamHandleLike,
  ThoughtStep,
  ToolCall
} from '@portal/app/shared/components/ai-assistant-panel/types/chat-block.types';

@Injectable()
export class AiChatService {
  readonly #injector = inject(EnvironmentInjector);

  #stream: StreamHandleLike | null = null;
  #pendingRunId: string | null = null;

  readonly #lsRunId = signal<string | null>(null);
  readonly #messageMeta = signal<ReadonlyMap<string, MessageMeta>>(new Map());
  readonly #errorMessages = signal<ChatMessage[]>([]);

  /** Most recent agent run ID. Used to submit feedback. */
  readonly lsRunId = this.#lsRunId.asReadonly();

  readonly messages = computed<ChatMessage[]>(() => {
    const stream = this.#stream;
    if (!stream) return this.#errorMessages();
    return [
      ...buildMessages(stream, this.#messageMeta()),
      ...this.#errorMessages()
    ];
  });

  readonly displayBlocks = computed<ChatBlock[]>(() =>
    buildDisplayBlocks(this.messages())
  );

  readonly isLoading = computed(() => !!this.#stream?.isLoading());

  readonly streamingDraft = computed(() => {
    const stream = this.#stream;
    if (!stream || !stream.isLoading()) return '';
    const msgs = stream.messages();
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m = msgs[i];
      if (m && m.type === 'ai') return contentToText(m.content);
    }
    return '';
  });

  readonly liveToolCalls = computed<ToolCall[]>(() => {
    const stream = this.#stream;
    if (!stream || !stream.isLoading()) return [];
    return liveToolCallsFor(stream);
  });

  readonly liveSteps = computed<readonly ThoughtStep[]>(() => {
    const tools = this.liveToolCalls();
    if (tools.length === 0) return [];
    return [{ kind: 'tools', id: 'live-tools', tools }];
  });

  handleRunMetadata(runId: string): void {
    this.#pendingRunId = runId;
    this.#lsRunId.set(runId);
  }

  /** Out-of-band error path. In-stream errors come through `stream.error()`. */
  handleError(err: unknown): void {
    const message =
      err instanceof Error ? err.message : 'Failed to get response.';
    this.#errorMessages.update((msgs) => [
      ...msgs,
      {
        id: `error:${Date.now()}`,
        role: 'error',
        content: message,
        timestamp: Date.now()
      }
    ]);
  }

  clear(): void {
    this.#stream?.switchThread(null);
    this.#lsRunId.set(null);
    this.#pendingRunId = null;
    this.#messageMeta.set(new Map());
    this.#errorMessages.set([]);
  }

  bind(stream: StreamHandleLike): void {
    this.#stream = stream;

    runInInjectionContext(this.#injector, () => {
      let prevLoading = stream.isLoading();
      effect(() => {
        const loading = stream.isLoading();
        if (loading === prevLoading) return;
        prevLoading = loading;
        if (!loading) this.#finalizeTurn(stream);
      });
    });
  }

  #finalizeTurn(stream: StreamHandleLike): void {
    const target = findFinalizeTarget(stream.messages());
    if (!target?.id) return;
    const targetId = target.id;

    const lsRunId = this.#pendingRunId ?? undefined;
    if (lsRunId) this.#lsRunId.set(lsRunId);
    this.#pendingRunId = null;

    this.#messageMeta.update((m) => {
      const next = new Map(m);
      next.set(targetId, { lsRunId });
      return next;
    });
  }
}
